/* ==========================================================================
   Taana — animations.js
   Declarative, section-scoped GSAP system.

   Markup API
     data-animate="fade-up|fade|clip|image|stagger|scale|text"   one-shot reveals
     data-animate-delay="0.2"                                     optional delay (s)
     data-parallax="0.15"                                         scrubbed y parallax (-1..1)
     data-scene="horizontal|fabric-to-form|sticky-story|layered-depth|cinematic-reveal|image-sequence|manifesto"
                                                                  multi-step scroll scenes

   Lifecycle
     Every .shopify-section gets its own gsap.context(). The context is reverted on
     shopify:section:unload and rebuilt on shopify:section:load, so re-render,
     duplicate and reorder never leave orphaned ScrollTriggers or listeners.
   ========================================================================== */
(() => {
  const Theme = (window.Theme = window.Theme || {});
  const root = document.documentElement;
  const settings = Theme.settings || {};
  const reduced =
    settings.forceReducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const revealAll = (scope = document) =>
    scope.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-animated'));

  if (!window.gsap || !window.ScrollTrigger || !settings.animations || reduced) {
    root.classList.remove('js-motion');
    revealAll();
    return;
  }

  const { gsap, ScrollTrigger } = window;
  gsap.registerPlugin(ScrollTrigger);
  gsap.config({ nullTargetWarn: false });
  root.classList.add('js-motion');

  const css = getComputedStyle(root);
  const DIST = parseFloat(css.getPropertyValue('--motion-distance')) || 32;
  const DUR = parseFloat(css.getPropertyValue('--motion-duration')) || 1;
  const PARALLAX = (Number(settings.parallax) || 0) / 10; // 0..2
  const EASE = 'expo.out';
  const START = 'top 88%';

  const done = (el) => () => el.classList.add('is-animated');
  const delayOf = (el) => parseFloat(el.dataset.animateDelay) || 0;

  /* ---------- Text splitting (words, preserves inline markup like <em>) ---------- */
  function splitWords(el) {
    if (el.dataset.split === 'true') return el.querySelectorAll('.split-word__inner');
    el.dataset.originalHtml = el.innerHTML;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        const outer = document.createElement('span');
        outer.className = 'split-word';
        const inner = document.createElement('span');
        inner.className = 'split-word__inner';
        inner.textContent = part;
        outer.appendChild(inner);
        frag.appendChild(outer);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.dataset.split = 'true';
    el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
    return el.querySelectorAll('.split-word__inner');
  }
  function unsplit(el) {
    if (el.dataset.originalHtml !== undefined) {
      el.innerHTML = el.dataset.originalHtml;
      delete el.dataset.originalHtml;
      delete el.dataset.split;
      el.removeAttribute('aria-label');
    }
  }

  /* ---------- One-shot reveal presets ---------- */
  const presets = {
    'fade-up'(el) {
      return gsap.fromTo(el, { y: DIST, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, duration: DUR, delay: delayOf(el), ease: EASE,
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
    fade(el) {
      return gsap.fromTo(el, { autoAlpha: 0 }, {
        autoAlpha: 1, duration: DUR, delay: delayOf(el), ease: 'power2.out',
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
    clip(el) {
      return gsap.fromTo(el, { clipPath: 'inset(0% 0% 100% 0%)' }, {
        clipPath: 'inset(0% 0% 0% 0%)', duration: DUR * 1.2, delay: delayOf(el), ease: 'expo.inOut',
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
    image(el) {
      const img = el.querySelector('img, video, svg');
      const tl = gsap.timeline({
        delay: delayOf(el),
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        onComplete: done(el),
      });
      tl.fromTo(el, { clipPath: 'inset(12% 8% 12% 8%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: DUR * 1.4, ease: 'expo.inOut' }, 0);
      if (img) tl.fromTo(img, { scale: 1.18 }, { scale: 1, duration: DUR * 1.8, ease: EASE, clearProps: 'transform' }, 0);
      return tl;
    },
    scale(el) {
      return gsap.fromTo(el, { scale: 0.94, autoAlpha: 0 }, {
        scale: 1, autoAlpha: 1, duration: DUR, delay: delayOf(el), ease: EASE,
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
    stagger(el) {
      const items = el.querySelectorAll(':scope > *');
      gsap.set(el, { autoAlpha: 1 });
      return gsap.fromTo(items, { y: DIST, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, duration: DUR, ease: EASE, stagger: { each: 0.08, from: 'start' },
        delay: delayOf(el),
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
    text(el) {
      const words = splitWords(el);
      gsap.set(el, { autoAlpha: 1 });
      return gsap.fromTo(words, { yPercent: 110 }, {
        yPercent: 0, duration: DUR * 1.1, ease: EASE, stagger: 0.045, delay: delayOf(el),
        scrollTrigger: { trigger: el, start: START, once: true }, onComplete: done(el),
      });
    },
  };

  /* ---------- Scroll scenes (multi-step, scrubbed) ---------- */
  const scenes = {
    /* Pinned horizontal gallery on desktop; native scroll-snap on touch/mobile. */
    horizontal(el, mm) {
      const track = el.querySelector('[data-horizontal-track]');
      if (!track) return;
      mm.add('(min-width: 990px)', () => {
        el.classList.add('is-pinned-scene');
        const distance = () => Math.max(0, track.scrollWidth - el.clientWidth);
        if (distance() < 40) {
          el.classList.remove('is-pinned-scene');
          return;
        }
        gsap.to(track, {
          x: () => -distance(),
          ease: 'none',
          scrollTrigger: {
            trigger: el, start: 'top top', end: () => `+=${distance()}`, pin: true, scrub: 0.6,
            invalidateOnRefresh: true, anticipatePin: 1,
          },
        });
        return () => el.classList.remove('is-pinned-scene');
      });
    },

    /* Fabric → Detail → Craft → Silhouette: chapters crossfade while pinned. */
    'fabric-to-form'(el, mm) {
      const stage = el.querySelector('[data-scene-stage]');
      const frames = gsap.utils.toArray(el.querySelectorAll('[data-scene-frame]'));
      const texts = gsap.utils.toArray(el.querySelectorAll('[data-scene-text]'));
      const progress = el.querySelector('[data-scene-progress]');
      if (!stage || frames.length < 2) return;
      mm.add('(min-width: 990px)', () => {
        el.classList.add('is-pinned-scene');
        gsap.set(frames.slice(1), { autoAlpha: 0, scale: 1.08 });
        gsap.set(texts.slice(1), { autoAlpha: 0, y: DIST });
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el, start: 'top top', end: () => `+=${frames.length * 85}%`, pin: stage, scrub: 0.8,
            onUpdate: (self) => progress && progress.style.setProperty('--p', self.progress.toFixed(3)),
          },
        });
        frames.forEach((frame, i) => {
          if (i === 0) {
            tl.to(frame.querySelector('img, svg'), { scale: 1.12, ease: 'none', duration: 1 }, 0);
            return;
          }
          tl.to(frames[i - 1], { autoAlpha: 0, duration: 0.5 }, i)
            .to(frame, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'power2.out' }, i - 0.1)
            .to(texts[i - 1] || {}, { autoAlpha: 0, y: -DIST, duration: 0.4 }, i - 0.05)
            .to(texts[i] || {}, { autoAlpha: 1, y: 0, duration: 0.5 }, i + 0.1);
        });
        return () => el.classList.remove('is-pinned-scene');
      });
    },

    /* Sticky media swaps as each story chapter becomes active. */
    'sticky-story'(el) {
      const chapters = gsap.utils.toArray(el.querySelectorAll('[data-story-chapter]'));
      const medias = gsap.utils.toArray(el.querySelectorAll('[data-story-media]'));
      if (!chapters.length || !medias.length) return;
      const activate = (i) => {
        medias.forEach((m, idx) => m.classList.toggle('is-active', idx === i));
        chapters.forEach((c, idx) => c.classList.toggle('is-active', idx === i));
      };
      chapters.forEach((chapter, i) => {
        ScrollTrigger.create({
          trigger: chapter, start: 'top 60%', end: 'bottom 60%',
          onToggle: (self) => self.isActive && activate(i),
        });
      });
    },

    /* Layers move at different speeds: data-depth="0.2" etc. */
    'layered-depth'(el, mm) {
      mm.add('(min-width: 750px)', () => {
        el.querySelectorAll('[data-depth]').forEach((layer) => {
          const depth = parseFloat(layer.dataset.depth) || 0.1;
          gsap.fromTo(layer, { yPercent: depth * 60 * Math.max(PARALLAX, 0.5) }, {
            yPercent: -depth * 60 * Math.max(PARALLAX, 0.5), ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
          });
        });
      });
    },

    /* Full-bleed media expands from an inset frame to the viewport. */
    'cinematic-reveal'(el, mm) {
      const frame = el.querySelector('[data-reveal-frame]');
      if (!frame) return;
      mm.add('(min-width: 750px)', () => {
        gsap.fromTo(frame, { clipPath: 'inset(14% 18% 14% 18%)' }, {
          clipPath: 'inset(0% 0% 0% 0%)', ease: 'none',
          scrollTrigger: { trigger: el, start: 'top 75%', end: 'center center', scrub: 0.6 },
        });
      });
    },

    /* Image sequence: frames stack and advance with scroll. */
    'image-sequence'(el, mm) {
      const frames = gsap.utils.toArray(el.querySelectorAll('[data-sequence-frame]'));
      const stage = el.querySelector('[data-scene-stage]');
      if (frames.length < 2 || !stage) return;
      mm.add('(min-width: 750px)', () => {
        gsap.set(frames.slice(1), { yPercent: 100 });
        const tl = gsap.timeline({
          scrollTrigger: { trigger: el, start: 'top top', end: () => `+=${frames.length * 70}%`, pin: stage, scrub: 0.7 },
        });
        frames.slice(1).forEach((f, i) => tl.to(f, { yPercent: 0, ease: 'none', duration: 1 }, i));
      });
    },

    /* Scrolling typography: lines drift in opposite directions with scroll. */
    'scroll-type'(el) {
      el.querySelectorAll('[data-scroll-line]').forEach((line) => {
        const dir = Number(line.dataset.scrollLine) || -1;
        gsap.fromTo(line, { xPercent: dir < 0 ? 0 : -25 }, {
          xPercent: dir < 0 ? -25 : 0, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.4 },
        });
      });
    },

    /* Manifesto: words brighten as they scroll through the viewport. */
    manifesto(el) {
      const target = el.querySelector('[data-manifesto-text]');
      if (!target) return;
      const words = splitWords(target);
      gsap.set(target, { autoAlpha: 1 });
      gsap.fromTo(words, { opacity: 0.16 }, {
        opacity: 1, ease: 'none', stagger: 0.1,
        scrollTrigger: { trigger: target, start: 'top 80%', end: 'bottom 45%', scrub: 0.5 },
      });
      return () => unsplit(target);
    },
  };

  /* ---------- Section contexts ---------- */
  const contexts = new Map();

  function initSection(section) {
    if (!section || !section.id) return;
    destroySection(section.id);
    const cleanups = [];
    const mm = gsap.matchMedia();
    const ctx = gsap.context(() => {
      section.querySelectorAll('[data-animate]').forEach((el) => {
        if (el.classList.contains('is-animated')) return;
        const fn = presets[el.dataset.animate];
        if (fn) fn(el);
        else el.classList.add('is-animated');
        if (el.dataset.animate === 'text') cleanups.push(() => unsplit(el));
      });

      if (PARALLAX > 0) {
        mm.add('(min-width: 750px)', () => {
          section.querySelectorAll('[data-parallax]').forEach((el) => {
            const speed = (parseFloat(el.dataset.parallax) || 0.15) * PARALLAX;
            gsap.fromTo(el, { yPercent: -speed * 12 }, {
              yPercent: speed * 12, ease: 'none',
              scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true },
            });
          });
        });
      }

      section.querySelectorAll('[data-scene]').forEach((el) => {
        const scene = scenes[el.dataset.scene];
        if (!scene) return;
        const cleanup = scene(el, mm);
        if (typeof cleanup === 'function') cleanups.push(cleanup);
      });
    }, section);
    contexts.set(section.id, { ctx, mm, cleanups });
  }

  function destroySection(id) {
    const entry = contexts.get(id);
    if (!entry) return;
    entry.mm.revert();
    entry.ctx.revert();
    entry.cleanups.forEach((fn) => fn());
    contexts.delete(id);
  }

  const sectionsIn = (scope) => {
    if (scope.classList && scope.classList.contains('shopify-section')) return [scope];
    return Array.from(scope.querySelectorAll('.shopify-section'));
  };

  const refresh = Theme.utils ? Theme.utils.debounce(() => ScrollTrigger.refresh(), 200) : () => ScrollTrigger.refresh();

  function boot() {
    sectionsIn(document).forEach(initSection);
    // Layout can shift once fonts and lazy images arrive; refresh trigger positions.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    window.addEventListener('load', refresh, { once: true });
  }

  document.addEventListener('shopify:section:load', (e) => {
    sectionsIn(e.target).forEach(initSection);
    refresh();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    sectionsIn(e.target).forEach((s) => destroySection(s.id));
    refresh();
  });
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:select', (e) => {
    // In the editor, make the selected section fully visible immediately.
    e.target.querySelectorAll('[data-animate]').forEach((el) => {
      if (!el.classList.contains('is-animated')) {
        gsap.set(el, { clearProps: 'all' });
        el.querySelectorAll('.split-word__inner').forEach((w) => gsap.set(w, { clearProps: 'all' }));
        el.classList.add('is-animated');
      }
    });
  });

  // Overlays (drawers/modals) change nothing in flow, but late content (filters,
  // recommendations) does. Components dispatch this to re-measure.
  document.addEventListener('taana:layout-change', refresh);

  Theme.motion = { init: initSection, destroy: destroySection, refresh, presets, scenes, splitWords, unsplit };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
